import type { OversightRegisterResponse } from '@risksmart-app/trpc/src/types';
import type { GetOversightAssessmentsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetOversightAssessmentsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

/**
 * Reshapes the register response into the GraphQL query shape so both branches
 * feed consumers identical data.
 *
 * The legacy GraphQL selected `assessmentResults` as `assessedItems`, with each
 * leg reached through a `parents` array filtered by ParentType. Drizzle cannot
 * alias one relation twice, so the register query config selects `risk` /
 * `obligation` / `document` directly alongside the three result rows, and the
 * one-element `parents` arrays are synthesised here.
 *
 * `riskAssessmentResult` maps from `controlledRiskAssessmentResult`: the
 * register only ever surfaced the controlled (residual) leg under that field.
 *
 * `assessmentResults` is destructured out rather than spread through, so the
 * mapped row carries only `assessedItems` — the field the GraphQL query
 * actually returns. Leaving both on the object would make the two branches
 * observably different, which defeats the point of the bridge.
 */
export const mapOversightRegisterToGraphQL = (
  data: OversightRegisterResponse
): GetOversightAssessmentsQuery => ({
  oversight_assessment: data.oversight.map(({ assessmentResults, ...item }) => ({
    ...item,
    assessedItems: assessmentResults.map((ar) => ({
      riskAssessmentResult: ar.controlledRiskAssessmentResult
        ? {
            Id: ar.controlledRiskAssessmentResult.Id,
            parents: [{ risk: ar.risk }],
          }
        : null,
      obligationAssessmentResult: ar.obligationAssessmentResult
        ? {
            Id: ar.obligationAssessmentResult.Id,
            parents: [{ obligation: ar.obligation }],
          }
        : null,
      documentAssessmentResult: ar.documentAssessmentResult
        ? {
            Id: ar.documentAssessmentResult.Id,
            parents: [{ document: ar.document }],
          }
        : null,
    })),
  })),
});

/**
 * The oversight assessment register read.
 *
 * Named `...Register` after `useGetInternalAuditReportsRegister`, and
 * deliberately NOT `useGetOversightAssessments` — `useGetAllOversightAssessments`
 * already exists and wraps `frontend.oversight.getAll`, a much thinner flat
 * column subset with no relations.
 */
export const useGetOversightAssessmentsRegister = createQueryHook<
  Record<string, never>,
  OversightRegisterResponse,
  GetOversightAssessmentsQuery
>({
  trpcQueryOptions: (trpc) => trpc.frontend.oversight.register.queryOptions(),
  mapTrpcDataToGraphQL: mapOversightRegisterToGraphQL,
  graphqlDocument: GetOversightAssessmentsDocument,
});
